import { Typography } from "@mui/joy";
import React, { useEffect, useState } from "react";
import useApiService from "../../../hooks/useApiService";
import { RouteComponentProps } from "../../../models/RouteComponent";
import { Button } from "primereact/button";
import ButtonPanel from "../../../components/Shared/ButtonPanel";
import { ClientRecord } from "../../../models/ClientInfo";
import { Card } from "primereact/card";
import { Input, MultiSelect, Textarea } from "../../../components/Shared/Forms";
import ImageS3Upload from "../../../components/ImageS3Upload";
import { AddressSearch } from "../../../components/Shared/Address/AddressSearch";
import { useToast } from "../../../context/ToastContext";

type formDataType = {
  name: string;
  business_nature?: string;
  address?: string;
  location?: { lat: number; lng: number };
  photo?: string;
  industry: number[];
};

function GeneralDetails(props: RouteComponentProps) {
  const { session } = props;
  const toast = useToast();
  const [photo, setPhoto] = useState<string>();
  const [s3Key, setS3Key] = useState("");
  const [file, setFile] = useState<File>();
  const [formData, setFormData] = useState<formDataType>({
    name: "",
    industry: [],
  });
  const [somethingChanged, setSomethingChanged] = useState<boolean>(false);
  const [clientRecord, setClientRecord] = useState<ClientRecord>();

  const { loading: loadingClient, invalidate } = useApiService<ClientRecord>({
    url: `client/${session.client_id}`,
    method: "GET",
    apiName: "core",
    onSuccess: data => {
      setClientRecord(data);
      const addressLocation = data.physical_address_gps && data.physical_address_gps.split(",");

      let formValues: formDataType = {
        name: data.name,
        business_nature: data.business_nature,
        address: data.physical_address,
        industry: data.industry,
      };

      if (addressLocation && addressLocation.length > 0) {
        formValues["location"] = {
          lat: parseFloat(addressLocation[0]),
          lng: parseFloat(addressLocation[1]),
        };
      }
      setS3Key(data?.s3_key || `client/${session.client_id}`);
      setFormData(formValues);
    },
    cache: { enabled: true, strategy: "cache-first", ttlMs: 5 * 60_000 },
  });

  const { loading: updatingClient, fetchData: updateClient } = useApiService<ClientRecord>({
    fetchOnMount: false,
    url: `client/${session.client_id}`,
    method: "PUT",
    apiName: "core",
    onSuccess: data => {
      setSomethingChanged(false);
      setClientRecord(prev => ({
        ...prev,
        ...data,
      }));
      invalidate();
      toast("success", "Client record successfully updated");
    },
  });

  const { loading: loadingIndustries, data: industries } = useApiService<
    {
      created_at?: Date;
      deleted_at?: Date;
      id: number;
      name: string;
      updated_at?: Date;
    }[]
  >({
    url: "industry",
    method: "GET",
    apiName: "core",
    params: {
      table_view: true,
    },
  });

  useEffect(() => {
    props.setGlobalLoader(loadingClient);
  }, [loadingClient, props]);

  useEffect(() => {
    if (!file) return;
    props.setGlobalLoader(true);
    updateClient({
      payload: {
        s3_key: s3Key,
      },
    }).then(() => {
      props.setGlobalLoader(false);
    }).catch(() => {
      props.setGlobalLoader(false);
    });
  }, [file]);

  function cancelChanges() {
    if (clientRecord) {
      setSomethingChanged(false);
      setFormData(prev => ({
        ...prev,
        name: clientRecord.name,
        business_nature: clientRecord.business_nature,
        address: clientRecord.physical_address,
        industry: clientRecord.industry,
      }));
    }
  }

  async function handleSave() {
    let formValues: any = {
      ...formData,
      physical_address: formData.address,
      physical_address_gps: `${formData.location?.lat},${formData.location?.lng}`,
    };

    if (formValues) {
      delete formValues.address;
      delete formValues.location;
    }

    let updateObject = {
      ...clientRecord,
      ...formValues,
    };

    delete updateObject.consultant_name;
    delete updateObject.industry_list;
    delete updateObject.updated_at;
    delete updateObject.created_at;
    delete updateObject.photo;
    delete updateObject.deleted_at;

    await updateClient({
      payload: updateObject,
    });
  }

  return (
    <>
      <Typography level="h2" component="h1">
        General Details
      </Typography>
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "2rem" }}>
          <div style={{ width: "50%" }}>
            <Input
              label={"Company name"}
              name={"name"}
              value={formData.name}
              onChange={e => {
                setSomethingChanged(true);
                setFormData(prev => ({
                  ...prev,
                  name: e.target.value,
                }));
              }}
            />
            <Textarea
              label={"Business nature"}
              name={"business_nature"}
              value={formData.business_nature}
              onChange={e => {
                setSomethingChanged(true);
                setFormData(prev => ({
                  ...prev,
                  business_nature: e.target.value,
                }));
              }}
            />
          </div>
          <div style={{ width: "50%" }}>
            <ImageS3Upload
              photo={photo}
              setPhoto={setPhoto}
              s3Key={s3Key}
              file={file}
              setFile={setFile}
            />
          </div>
        </div>
        <MultiSelect
          options={
            industries?.map(industry => ({
              label: industry.name,
              value: industry.id,
            })) ?? []
          }
          disabled={true}
          showSelectAll={false}
          label={"Industry"}
          placeholder={"Select industry"}
          value={formData.industry}
          onChange={e => {
            setSomethingChanged(true);
            setFormData(prev => ({
              ...prev,
              industry: e.value,
            }));
          }}
          loading={loadingIndustries}
        />
        <AddressSearch
          includeMap={true}
          initialValue={formData.address}
          location={formData.location}
          handleChange={event => {
            setSomethingChanged(true);
            setFormData({
              ...formData,
              address: event.address,
              location: event.location,
            });
          }}
        />
      </Card>
      <ButtonPanel inModal={false}>
        <Button
          label={"Cancel"}
          outlined={true}
          disabled={!somethingChanged || updatingClient}
          onClick={cancelChanges}
        />
        <Button
          label={"Save"}
          loading={updatingClient}
          disabled={!somethingChanged}
          onClick={async () => {
            await handleSave();
          }}
        />
      </ButtonPanel>
    </>
  );
}

export default GeneralDetails;
